import {
  AnimateHeightBox,
  Body,
  Divider,
  Icon,
  Stack,
  vFlex,
} from "@biom3/react";
import { useMemo } from "react";
import { useCampaign } from "../CampaignProvider";
import type { CampaignQuestGroup, CampaignQuestState } from "../types";
import { CampaignQuestItem } from "./CampaignQuestItem";

export const CampaignQuestList = ({
  quests,
  onQuestClick,
  isWishlisted,
}: {
  quests: CampaignQuestGroup[];
  onQuestClick: (quest: CampaignQuestState) => void;
  isWishlisted: boolean;
}) => {
  let highlightedQuest = false;
  const { referralAccepted } = useCampaign();

  // Check if any quest has perpetualRewardsEligible flag
  const hasPerpetualRewardsEligibleQuest = useMemo(
    () => quests.some((g) => g.quests.some((q) => q.perpetualRewardsEligible)),
    [quests],
  );

  return (
    <Stack
      gap="base.spacing.x4"
      sx={{
        p: "base.spacing.x4",
        borderRadius: "base.borderRadius.x4",
        background: "base.color.translucent.emphasis.200",
      }}
    >
      {quests.map((group, index) => {
        return (
          <AnimateHeightBox
            key={group.name === "Start here" ? "start-here" : group.name}
            innerContainerSx={{ ...vFlex, gap: "base.spacing.x2" }}
          >
            <Divider
              size="xSmall"
              sx={{
                mt: index !== 0 ? "base.spacing.x4" : "",
                mb: "base.spacing.x2",
              }}
            >
              {group.name}
            </Divider>

            {group.quests.map((quest: CampaignQuestState) => {
              // Highlight first incomplete quest
              const highlight = !quest.completed && !highlightedQuest;
              if (!quest.completed) {
                highlightedQuest = true;
              }
              return (
                <CampaignQuestItem
                  key={quest.externalId}
                  quest={quest}
                  onQuestClick={onQuestClick}
                  isWishlisted={isWishlisted}
                  isHighlighted={highlight}
                  referralAccepted={referralAccepted}
                />
              );
            })}
          </AnimateHeightBox>
        );
      })}
      {hasPerpetualRewardsEligibleQuest && (
        <Stack direction="row" gap="base.spacing.x2" alignItems="center">
          <Icon
            icon="ImxRewards"
            variant="bold"
            sx={{
              width: "base.icon.size.250",
              fill: "base.gradient.1",
              pt: "base.spacing.x2",
            }}
          />
          <Body
            size="xSmall"
            weight="bold"
            sx={{
              color: "base.gradient.1",
              pt: "base.spacing.x2",
            }}
          >
            Gems will be rewarded instantly once eligible quests are completed.
          </Body>
        </Stack>
      )}
    </Stack>
  );
};
